/**
 * Catalog rows from a read, reconciled with the page's own writes (plan
 * §5.4). A read that started before a write can lag behind it: rows we
 * wrote since keep our version, rows we created since stay although the
 * read lacks them, and rows we deleted since stay deleted.
 */

interface IRow {
  id: number;
}

function byId<T extends IRow>(rows: readonly T[]): Map<number, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

/** Same content: the stored object is kept, so views keyed on objects stay put. */
export function shareRows<T extends IRow>(
  next: readonly T[],
  current: readonly T[] | null
): T[] {
  if (!current) return [...next];
  const stored = byId(current);
  return next.map((row) => {
    const existing = stored.get(row.id);
    return existing && JSON.stringify(existing) === JSON.stringify(row)
      ? existing
      : row;
  });
}

export interface IWriteSeqs {
  /** Our last write (upsert or delete) of each row. */
  written: Readonly<Record<number, number>>;
  /** Our deletes. */
  deleted: Readonly<Record<number, number>>;
}

/** `requestSeq`: the write sequence when the read started. */
export function reconcileRows<T extends IRow>(
  loaded: readonly T[],
  current: readonly T[] | null,
  seqs: IWriteSeqs,
  requestSeq: number
): T[] {
  const stored = byId(current ?? []);
  const newer = (id: number) => (seqs.written[id] ?? 0) > requestSeq;
  const deleted = (id: number) => (seqs.deleted[id] ?? 0) > requestSeq;
  const rows = loaded
    .filter((row) => !deleted(row.id))
    .map((row) => (newer(row.id) ? stored.get(row.id) ?? row : row));
  const loadedIds = new Set(rows.map((row) => row.id));
  const created = (current ?? []).filter(
    (row) => !loadedIds.has(row.id) && newer(row.id) && !deleted(row.id)
  );
  return [...rows, ...created];
}
